import { Box, CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { netScotlandTheme } from '../theme/netScotlandTheme';
import { PublicLayout } from './PublicLayout';
import PublicCalendar from './PublicCalendar';

/**
 * `/calendar` — picks the wrapper, then renders the same calendar either way.
 *
 * `?embed=1` is how the calendar goes onto netscotland.org: an iframe pointed
 * here renders the events and NOTHING else, so the host page's own header,
 * navigation and background are all that frame it. Without the parameter this
 * route behaves exactly as it always has — full PublicLayout with the logo and
 * footer — so no existing link changes.
 *
 * `?view=list` swaps the month grid for a date list, which is what actually fits
 * a sidebar. Anything else falls back to the grid rather than erroring: a
 * mistyped URL on someone's website should still show a calendar.
 *
 * The embed wrapper deliberately does NOT reuse PublicLayout — it needs the
 * opposite of what PublicLayout provides (no 100vh, no logo, no footer, no
 * opaque page background). It keeps CssBaseline for consistent fonts but forces
 * the body transparent so the host page's own background shows through instead
 * of a grey slab.
 *
 * Framing note: the app sets no X-Frame-Options / CSP frame-ancestors header
 * today, which is why this works at all. Adding a blanket one later WILL break
 * every embed on the charity's website — if that hardening happens it must
 * exempt this route.
 */
export const PublicCalendarRoute = () => {
    const [params] = useSearchParams();
    const embed = params.get('embed') === '1';
    const initialView = params.get('view') === 'list' ? 'list' : 'month';

    if (!embed) {
        return (
            <PublicLayout>
                <PublicCalendar />
            </PublicLayout>
        );
    }

    return (
        <ThemeProvider theme={netScotlandTheme}>
            <CssBaseline />
            <GlobalStyles
                styles={{
                    // Let the embedding page's background show through, and
                    // suppress the outer scrollbar the iframe height already
                    // accounts for.
                    'html, body': {
                        backgroundColor: 'transparent',
                        margin: 0,
                    },
                }}
            />
            <Box sx={{ p: 1 }}>
                <PublicCalendar embed initialView={initialView} />
            </Box>
        </ThemeProvider>
    );
};

export default PublicCalendarRoute;
